import React, { useEffect, useState } from "react";
import { useAudio } from "../../providers/AudioProvider";
import { useIsTyping } from "../../utils/typingGate";
import { MAX_CHARACTERS } from "../../utils/constants";

interface SpokenMessageItemProps {}

const SpokenMessageItem: React.FC<SpokenMessageItemProps> = () => {
  const isTyping = useIsTyping();
  const { transcript, mediaState } = useAudio();
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  // Subscribe to audio playing state
  useEffect(() => {
    const unsubscribe = mediaState.subscribe(() => {
      const state = mediaState.getState();
      setIsAudioPlaying(!!state.currentId && state.status === "playing");
    });
    return unsubscribe;
  }, [mediaState]);

  const getInstructionText = () => {
    if (isTyping) {
      return "";
    }
    if (isAudioPlaying) {
      return "Say 'Stop Talking' to interrupt";
    }
    return "Say 'Send Message' to send:";
  };

  const getTranscriptionText = () => {
    if (isTyping) {
      return "Please wait...";
    }
    return transcript;
  };

  const getCharacterCount = () => {
    if (isTyping || !transcript) return 0;
    return transcript.length;
  };

  const isNearLimit = () => {
    const count = getCharacterCount();
    return count > MAX_CHARACTERS * 0.8; // Show warning at 80% of limit
  };

  return (
    <div className="flex flex-col gap-2  sm:px-3 lg:px-4">
      {/* Instruction text */}
      <div className="text-xs sm:text-sm text-gray-600 font-medium text-end px-2">
        {getInstructionText()}
      </div>

      {/* Live transcription message */}
      <div className="flex justify-end">
        <div className="max-w-[80%] sm:max-w-[70%] lg:max-w-[60%] px-2">
          <div className="relative flex">
            <div
              className={`bg-white border-2 rounded-2xl px-3 sm:px-4 py-2 sm:py-3 shadow-sm ${
                isTyping ? "border-[#E38C76]" : "border-[#46ACC6]"
              }`}
              style={{
                borderBottomRightRadius: "0.2rem",
                borderTopRightRadius: "1.5rem",
                borderTopLeftRadius: "1.5rem",
                borderBottomLeftRadius: "1.5rem",
              }}
            >
              <div
                className={`text-sm sm:text-base break-words ${
                  isTyping ? "text-[#E38C76]" : "text-[#46ACC6]"
                }`}
              >
                {getTranscriptionText()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Character count and warning */}
      {!isTyping && transcript && (
        <div className="flex justify-end px-2">
          <div className="text-xs text-gray-500">
            <span className={isNearLimit() ? "text-orange-500" : ""}>
              {getCharacterCount()}/{MAX_CHARACTERS}
            </span>
            {isNearLimit() && (
              <span className="ml-2 text-orange-500">
                (Message will be trimmed)
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SpokenMessageItem;
